import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IdName } from '@api/models/idname';
import { PaginatorParams } from '@api/models/paginator';
import { GenericDialogComponent } from '@shared/generic-dialog/generic-dialog.component';
import { DateTime } from 'luxon';
import { TranslateConfigService } from 'src/app/core/translate-config.service';
import {
  COLUMN_LABELS,
  distinctVehiclesTableHeaders,
  emptyRowExposures,
  emptyRowPeopleCount,
  FILTER_MAPPING,
  targetGroupTableHeaders,
} from '../reports-hardcoded';
import { ReachReportTypeEnum } from '../../reach/model/reach-report-type-enum';
import { MediaTypeEnum, ReportsResponse } from '@api/index';
import { ActiveReportEnum } from '@api/models/active-report-enum';
import { currencyIdToCode, pricingModelIdToCode } from '../../statistics.utils';

@Component({
  selector: 'flow-reports-data',
  templateUrl: './reports-data.component.html',
  styleUrls: ['./reports-data.component.scss'],
})
export class ReportsDataComponent implements OnChanges, AfterViewInit {
  private sort: MatSort;
  @Input() activeReport: ActiveReportEnum;
  @Input() reports: ReportsResponse;
  @Input() includeSmsDetailsPOI: boolean;
  @Input() includeAdminDetailsPOI: boolean;
  @Input() includeVehicleDataReach: boolean;
  @Input() includeAverageTimeInChannel: boolean;
  @Input() showDetailedReport: boolean;
  @Input() isAdmin: boolean;
  @Input() reachReportType: ReachReportTypeEnum;
  @Output() reportShown = new EventEmitter<boolean>();
  @Output() getNewReport = new EventEmitter();
  ActiveReportEnum = ActiveReportEnum;
  isIncludeAverageTimeInChannel: boolean = false;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
  }
  data;
  columns: string[] = ['date', 'customerName', 'channelName', 'screenName', 'placement'];
  displayedColumns: string[] = [...this.columns];
  dataSource: MatTableDataSource<ReportsTable> = new MatTableDataSource([]);
  timeRanges: string[] = [];
  MediaTypeEnum = MediaTypeEnum;
  params: PaginatorParams = {
    pageNumber: 0,
    pageSize: 10,
    sortBy: 'Date',
    sortDirection: 'asc',
  };
  pageSizeOptions = [5, 10, 25, 100];
  totalCount: number = 0;
  trend = true;
  nonSortableColumns: string[] = ['airtime', 'impression', 'menAverageTimeInChannel', 'womenAverageTimeInChannel', 'smsSentTo'];

  filterMapping = FILTER_MAPPING;
  headerLabels: Record<string, string> = {};

  constructor(
    private translateConfigService: TranslateConfigService,
    private dialog: MatDialog
  ) {}

  ngOnChanges(): void {
    this.createTimeRanges();

    this.displayedColumns = [...this.columns];
    this.data =
      this.reports?.exposureData ||
      this.reports?.peopleCounterDates ||
      this.reports?.reachDates ||
      this.reports?.personOfInterestStatisticsReports;

    if (this.data?.length > 0) {
      this.updateDisplayedColumns();
      this.updateDataSource(this.data);
    }

    if (this.reports.totalCount) {
      this.totalCount = this.reports.totalCount;
    }

    if (this.reports.currentPage) {
      this.params = { ...this.params, pageNumber: this.reports.currentPage };
    }

    this.getHeaderLabel(this.displayedColumns);
  }

  getHeaderLabel(columns: string[]): void {
    this.headerLabels = {};

    for (const column of columns) {
      if (column === 'screenName') {
        this.headerLabels[column] =
          this.activeReport === ActiveReportEnum.PeopleCounter
            ? this.translateConfigService.instant('reports.table.screenAndSensors')
            : this.translateConfigService.instant('reports.table.screen');
      } else if (column.startsWith('targetGroup')) {
        const index = +column.replace('targetGroup', '');
        const isWoman = index >= 1 && index <= 7;
        const ageGroupKey = ['under18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'][(index - 1) % 7];

        const gender = this.translateConfigService.instant(isWoman ? 'reports.table.targetGroups.women' : 'reports.table.targetGroups.men');
        const age = this.translateConfigService.instant(`reports.table.targetGroups.${ageGroupKey}`);

        this.headerLabels[column] = `${gender}: ${age}`;
      } else {
        this.headerLabels[column] = COLUMN_LABELS[column] ? this.translateConfigService.instant(COLUMN_LABELS[column]) : column;
      }
    }
  }

  ngAfterViewInit(): void {
    this.reportShown.emit(true);
  }

  private updateDisplayedColumns(): void {
    if (this.reports?.exposureData?.length > 0) {
      this.displayedColumns.push(
        ...['bookingType', 'bookingNumber', 'bookingName', 'client', 'mediaFileName', 'fileType', 'totalExposures']
      );
    }

    if (this.reports?.peopleCounterDates?.length > 0) {
      const isSummarized = this.activeReport === ActiveReportEnum.PeopleCounter && !this.showDetailedReport;
      if (isSummarized) {
        this.displayedColumns = ['date', 'screenType', 'visitors'];
      } else {
        this.displayedColumns = this.displayedColumns.concat(['screenType', 'visitors']);
      }
      if (this.activeReport == ActiveReportEnum.PeopleCounter && this.includeAverageTimeInChannel) {
        this.isIncludeAverageTimeInChannel = true;
        this.displayedColumns.push('menAverageTimeInChannel', 'womenAverageTimeInChannel');
      }
    }

    if (this.reports?.reachDates?.length > 0) {
      const columnName = this.reachReportType === ReachReportTypeEnum.ReachPeople ? 'visitors' : 'vehicles';
      this.displayedColumns.push(columnName);
    }
    if (this.reports?.personOfInterestStatisticsReports?.length > 0) {
      this.displayedColumns.push(...['screenType', 'category', 'personOfInterest']);
      if (this.includeAdminDetailsPOI) {
        this.displayedColumns.push('poiCreatedDate', 'createdBy');
      }
      if (this.includeSmsDetailsPOI) {
        this.displayedColumns.push('smsSentTo');
      }
    }

    if (this.reports.includePrimaryAndSecoundaryGroups) {
      this.displayedColumns.push('primaryExposures', 'secondaryExposures');
    }

    if (this.reports.includeSharedOfVoices) {
      this.displayedColumns.push('shareOfVoice');
    }

    if (this.reports.includeExtendedStatistics) {
      this.displayedColumns.push('airtime', 'attentionTime', 'impression');
    }

    if (this.reports.includeAdvertisingStatistics) {
      this.displayedColumns.push(
        'price',
        'currency',
        'pricingModel',
        'budgetedImpressions',
        'cppCampaignBudget',
        'totalCPP',
        'impressionsOutcome',
        'cppOutcome',
        'budgetedExposures',
        'cpvCampaignBudget',
        'totalCPV',
        'exposuresOutcomes',
        'cpvOutcomes'
      );
    }

    if (this.reports.includeReach) {
      this.displayedColumns.push('reach');
    }

    if (this.reports.includeDemographicalBreakdown) {
      if (!this.reachReportType || this.reachReportType === ReachReportTypeEnum.ReachPeople) {
        this.displayedColumns.push(...targetGroupTableHeaders);
      } else if (this.reachReportType === ReachReportTypeEnum.ReachVehicles) {
        this.displayedColumns.push(...distinctVehiclesTableHeaders);
      }
      if (this.activeReport != ActiveReportEnum.Exposures) {
        const column = this.reachReportType === ReachReportTypeEnum.ReachVehicles ? 'opportunityToSeeVehicles' : 'opportunityToSee';

        this.displayedColumns.push(column);
      }
    }

    if (this.reports.includeHourlyBreakdown) {
      this.displayedColumns.push(...this.timeRanges);
    }

    if (this.reports.personOfInterestStatisticsReports) {
    }

    const shouldIncludeCustomer = this.isAdmin || this.activeReport !== ActiveReportEnum.Reach;
    const isSummarizedPeopleCounter = this.activeReport === ActiveReportEnum.PeopleCounter && !this.showDetailedReport;

    const moveToEnd = ['customerName', 'channelName', 'placement'];
    const filteredMoveToEnd = moveToEnd.filter(col => {
      if (col === 'customerName') {
        return shouldIncludeCustomer;
      }
      return !isSummarizedPeopleCounter;
    });

    this.displayedColumns = this.displayedColumns
      .filter(col => !moveToEnd.includes(col))
      .concat(filteredMoveToEnd.filter(col => this.displayedColumns.includes(col) || this.columns.includes(col)));
  }

  private updateDataSource(data: any[]): void {
    if (!this.reports.personOfInterestStatisticsReports) {
      const shouldAddEmptyRow = this.activeReport !== ActiveReportEnum.Reach;
      const newEmptyRow =
        this.reports?.exposureData?.length > 0
          ? {
              ...emptyRowExposures,
              totalExposures: `${this.translateConfigService.instant('reports.table.total')}: ${this.reports?.totalReportCount}`,
            }
          : {
              ...emptyRowPeopleCount,
              visitors: `${this.translateConfigService.instant('reports.table.total')}: ${this.reports?.totalVisitors}`,
            };

      const updatedData = data.map(item => {
        let modifiedData = { ...item };

        if (this.reports.includeDemographicalBreakdown) {
          if (!this.reachReportType || this.reachReportType === ReachReportTypeEnum.ReachPeople) {
            modifiedData = {
              ...modifiedData,
              targetGroup1: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 1)[0]?.count,
              targetGroup2: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 2)[0]?.count,
              targetGroup3: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 3)[0]?.count,
              targetGroup4: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 4)[0]?.count,
              targetGroup5: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 5)[0]?.count,
              targetGroup6: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 6)[0]?.count,
              targetGroup7: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 7)[0]?.count,
              targetGroup8: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 8)[0]?.count,
              targetGroup9: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 9)[0]?.count,
              targetGroup10: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 10)[0]?.count,
              targetGroup11: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 11)[0]?.count,
              targetGroup12: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 12)[0]?.count,
              targetGroup13: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 13)[0]?.count,
              targetGroup14: item?.distinctTargetGroups?.filter(tg => tg.targetGroup == 14)[0]?.count,
            };
          } else if (this.reachReportType === ReachReportTypeEnum.ReachVehicles) {
            modifiedData = {
              ...modifiedData,
              cars: item?.distinctVehicles?.filter(tg => tg.vehicleType == 0)[0]?.count,
              vans: item?.distinctVehicles?.filter(tg => tg.vehicleType == 1)[0]?.count,
              buses: item?.distinctVehicles?.filter(tg => tg.vehicleType == 2)[0]?.count,
              trucks: item?.distinctVehicles?.filter(tg => tg.vehicleType == 3)[0]?.count,
              opportunityToSee: item?.opportunityToSeeVehicles,
            };
          }
        }

        if (this.reports.includeHourlyBreakdown) {
          this.timeRanges.forEach((range, i) => {
            modifiedData[range] = item.hourlyCalculated ? item?.hourlyCalculated[i] : '';
          });
        }

        if (this.reports?.exposureData?.length > 0) {
          modifiedData.mediaFileName = item.mediaFile.name || item.mediaFile;
        }

        if (this.reports.includeAdvertisingStatistics) {
          modifiedData.pricingModel = pricingModelIdToCode(item.pricingModel);
          modifiedData.currency = currencyIdToCode(item.currency);
        }

        modifiedData.customerName = item.customer?.name || item.customer;
        modifiedData.channelName = item.channel?.name || item.channel;
        modifiedData.screenName = item.screen?.name || item.screen;
        modifiedData.client = item.client?.Name || item.clientName;
        return modifiedData;
      });

      if (shouldAddEmptyRow) {
        updatedData.push(newEmptyRow);
      }

      this.dataSource.data = updatedData;
    } else {
      this.dataSource.data = this.reports.personOfInterestStatisticsReports;
    }
  }

  private createTimeRanges(): void {
    this.timeRanges = [];
    for (let hour = 0; hour < 24; hour++) {
      const startHour = hour;
      const endHour = (hour + 1) % 24;
      const timeRange = `${DateTime.fromObject({ hour: startHour }).toFormat('HH:mm')} - ${DateTime.fromObject({ hour: endHour }).toFormat(
        'HH:mm'
      )}`;
      this.timeRanges.push(timeRange);
    }
  }

  handlePageEvent(e: PageEvent) {
    this.params = { ...this.params, pageNumber: e.pageIndex, pageSize: e.pageSize };
    const newParams: PaginatorParams & { reachReportType: ReachReportTypeEnum } = {
      ...this.params,
      reachReportType: this.reachReportType,
    };
    this.getNewReport.emit(newParams);
  }

  getTargetGroupKey(key: string) {
    return this.filterMapping[key] || key;
  }

  openSmsSentToDialog(sentTo: IdName[]): void {
    this.dialog.open(GenericDialogComponent, {
      width: '400px',
      data: {
        title: this.translateConfigService.instant('poi.table.notificationSentTo'),
        type: 'list',
        items: sentTo.map(recipient => recipient.name),
      },
    });
  }

  handleSortEvent(sort: Sort) {
    const sortBy = this.getTargetGroupKey(sort?.active);
    this.params = {
      ...this.params,
      pageNumber: 0,
      sortBy,
      sortDirection: sort.direction || (this.params.sortDirection == 'asc' ? 'desc' : 'asc') || 'asc',
    };
    const newParams: PaginatorParams & { reachReportType: ReachReportTypeEnum } = {
      ...this.params,
      reachReportType: this.reachReportType,
    };
    this.getNewReport.emit(newParams);
  }
}

export interface ReportsTable {
  [key: string]: any;
}
